import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { axiosInstance } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { fechaCorta, resumenPropuesta } from '@/lib/fichajes';

/**
 * Las correcciones que ha pedido la plantilla. Aprobar las escribe en el
 * registro como una corrección más (con el número de la solicitud); rechazar
 * exige explicarlo, y la persona recibe la respuesta por email.
 */
export function SolicitudesPendientes({ puedeResolver, alResolver }) {
  const [lista, setLista] = useState([]);
  const [rechazando, setRechazando] = useState(null);
  const [respuesta, setRespuesta] = useState('');
  const [ocupada, setOcupada] = useState(null);

  const cargar = useCallback(async () => {
    try {
      const r = await axiosInstance.get('/fichajes/solicitudes', { params: { estado: 'pendiente' } });
      setLista(r.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No se han podido cargar las solicitudes');
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const resolver = async (s, aprobar) => {
    setOcupada(s.id);
    try {
      const r = await axiosInstance.post(`/fichajes/solicitudes/${s.id}/resolver`, {
        aprobar, respuesta: aprobar ? null : respuesta,
      });
      toast.success(`${aprobar ? 'Aprobada' : 'Rechazada'}${r.data.email_enviado ? '. Se le ha avisado por email.' : ', pero el email no ha salido.'}`);
      setRechazando(null);
      setRespuesta('');
      cargar();
      alResolver();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No se ha podido resolver');
      cargar();
    } finally {
      setOcupada(null);
    }
  };

  if (!lista.length) return null;

  return (
    <div className="rounded-lg border border-sky-300 bg-sky-50 p-3 space-y-2">
      <p className="text-sm font-semibold text-sky-900">
        {lista.length} solicitud{lista.length > 1 ? 'es' : ''} de corrección pendiente{lista.length > 1 ? 's' : ''}
      </p>
      {lista.map((s) => (
        <div key={s.id} className="bg-white rounded-md border border-sky-200 p-2 text-sm space-y-1">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span>
              <b>{s.nombre}</b> · {fechaCorta(s.fecha)} · {resumenPropuesta(s)}
            </span>
            {puedeResolver && rechazando !== s.id && (
              <span className="flex gap-2">
                <Button size="sm" onClick={() => resolver(s, true)} disabled={ocupada === s.id}>Aprobar</Button>
                <Button size="sm" variant="outline" onClick={() => { setRechazando(s.id); setRespuesta(''); }}>
                  Rechazar
                </Button>
              </span>
            )}
          </div>
          <p className="text-slate-600">«{s.motivo}»</p>
          {rechazando === s.id && (
            <div className="flex gap-2">
              <Input value={respuesta} onChange={(e) => setRespuesta(e.target.value)}
                placeholder="Explícale por qué (le llega por email)" />
              <Button size="sm" variant="outline" onClick={() => setRechazando(null)}>Cancelar</Button>
              <Button size="sm" onClick={() => resolver(s, false)}
                disabled={!respuesta.trim() || ocupada === s.id}>Rechazar</Button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
